import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { FiArchive, FiRotateCcw } from 'react-icons/fi';
import {
  getBaseUrl,
  authedFetch,
  archiveChat,
  unarchiveChat,
  fetchAllPersonae,
  fetchSupportedLocalePairs,
  fetchStudentLocales,
  localeToIso,
  formatLocale,
  countryFlag,
  defaultStudentLocaleIso,
} from './api';
import { usePreferredLocale } from './PreferredLocaleContext';
import './ChatList.css';
import './PersonaList.css';

// Remembers the "show archived" choice across reloads. Archived chats are hidden by
// default, so without this a user working through their archive would have to re-enable
// the toggle on every visit. Kept separate from the persona list's own preference.
const SHOW_ARCHIVED_KEY = 'chatList.showArchived';

function readShowArchived() {
  try {
    return window.localStorage.getItem(SHOW_ARCHIVED_KEY) === 'true';
  } catch {
    // Private browsing and similar can make localStorage throw on access.
    return false;
  }
}

function writeShowArchived(value) {
  try {
    window.localStorage.setItem(SHOW_ARCHIVED_KEY, String(value));
  } catch {
    // Losing the preference is not worth failing the toggle over.
  }
}

// Inline form to create a new chat. Mirrors
// dev.c15u.autolang.web.api.dto.chat.ChatCreationRequest.
//
// The persona dictates the language being practised (its originalLocale, a.k.a.
// the persona locale), and /api/v0/locale tells us which persona locales are
// actually supported. Once a persona is chosen, /api/v0/locale/{personaLocale}
// gives the student locales that can be paired with it, which populate the
// "I speak" dropdown.
function CreateChatForm({ onCreated, onCancel }) {
  const { t } = useTranslation();
  const { preferredLocale } = usePreferredLocale();
  // Read the latest preferred locale and translator inside the locale-loading
  // effect without making them dependencies: re-running that effect on a late
  // profile fetch or a UI-language switch would refetch and clobber the user's
  // "I speak" selection.
  const preferredLocaleRef = useRef(preferredLocale);
  preferredLocaleRef.current = preferredLocale;
  const tRef = useRef(t);
  tRef.current = t;
  const [name, setName] = useState('');
  const [personaId, setPersonaId] = useState('');
  const [personae, setPersonae] = useState([]);
  const [personaQuery, setPersonaQuery] = useState('');
  const [studentLocales, setStudentLocales] = useState([]);
  const [studentLocaleIso, setStudentLocaleIso] = useState('');
  const [isLoadingLocales, setIsLoadingLocales] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load the personae and the supported persona locales together, then keep only
  // the personae whose locale the backend can actually teach.
  useEffect(() => {
    let cancelled = false;

    const loadPersonae = async () => {
      try {
        // include_public=true returns public personae in addition to the user's own.
        const [allPersonae, supportedPairs] = await Promise.all([
          fetchAllPersonae(),
          fetchSupportedLocalePairs(),
        ]);

        if (cancelled) return;

        const supportedPersonaLocales = new Set(
          supportedPairs.map((pair) => localeToIso(pair.personaLocale))
        );
        const list = allPersonae.filter((persona) =>
          supportedPersonaLocales.has(localeToIso(persona.originalLocale))
        );

        setPersonae(list);
        if (list.length > 0) {
          setPersonaId(list[0].id);
        }
      } catch (err) {
        if (!cancelled) setError(tRef.current('createChat.personaeLoadError'));
      }
    };

    loadPersonae();
    return () => {
      cancelled = true;
    };
  }, []);

  // Filter the loaded personae by the search box (client-side, over the already
  // locale-filtered list) so the dropdown narrows as the user types.
  const filteredPersonae = useMemo(() => {
    const query = personaQuery.trim().toLowerCase();
    if (!query) return personae;
    return personae.filter((persona) => persona.name.toLowerCase().includes(query));
  }, [personae, personaQuery]);

  // Keep the selection valid as the filter narrows: clear it when nothing matches,
  // and fall back to the first match when the current selection is filtered out.
  useEffect(() => {
    if (filteredPersonae.length === 0) {
      setPersonaId('');
    } else if (!filteredPersonae.some((persona) => persona.id === personaId)) {
      setPersonaId(filteredPersonae[0].id);
    }
  }, [filteredPersonae, personaId]);

  // Whenever the selected persona changes, refresh the student locales available
  // for that persona's locale.
  const selectedPersona = personae.find((persona) => persona.id === personaId);
  const personaLocaleIso = selectedPersona
    ? localeToIso(selectedPersona.originalLocale)
    : '';

  useEffect(() => {
    if (!personaLocaleIso) {
      setStudentLocales([]);
      setStudentLocaleIso('');
      return;
    }

    let cancelled = false;
    setIsLoadingLocales(true);

    const loadStudentLocales = async () => {
      try {
        const locales = await fetchStudentLocales(personaLocaleIso);
        if (cancelled) return;
        setStudentLocales(locales);
        // Default the "I speak" choice to the user's preferred language when it is
        // among the locales this persona supports, otherwise the first available.
        const preferred = defaultStudentLocaleIso(locales, preferredLocaleRef.current);
        setStudentLocaleIso(
          preferred || (locales.length > 0 ? localeToIso(locales[0]) : ''),
        );
      } catch (err) {
        if (!cancelled) {
          setStudentLocales([]);
          setStudentLocaleIso('');
          setError(tRef.current('createChat.personaLanguagesError'));
        }
      } finally {
        if (!cancelled) setIsLoadingLocales(false);
      }
    };

    loadStudentLocales();
    return () => {
      cancelled = true;
    };
  }, [personaLocaleIso]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const studentLocale = studentLocales.find(
      (locale) => localeToIso(locale) === studentLocaleIso
    );
    if (!studentLocale) {
      setError(t('createChat.chooseLanguage'));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await authedFetch(`${getBaseUrl()}/api/v0/chat`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          personaId,
          studentLocale: {
            languageCode: studentLocale.languageCode,
            countryCode: studentLocale.countryCode,
          },
        }),
      });

      if (response.ok) {
        onCreated();
      } else {
        setError(t('createChat.createFailed', { status: response.status }));
      }
    } catch (err) {
      setError(t('common.networkError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="create-chat-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor="chat-name">{t('createChat.name')}</label>
        <input
          type="text"
          id="chat-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          disabled={isSubmitting}
        />
      </div>
      <div className="form-group">
        <label htmlFor="chat-persona">{t('createChat.persona')}</label>
        <input
          type="text"
          id="chat-persona-search"
          className="persona-search"
          placeholder={t('createChat.searchPersonas')}
          value={personaQuery}
          onChange={(e) => setPersonaQuery(e.target.value)}
          disabled={isSubmitting || personae.length === 0}
        />
        <select
          id="chat-persona"
          value={personaId}
          onChange={(e) => setPersonaId(e.target.value)}
          required
          disabled={isSubmitting || filteredPersonae.length === 0}
        >
          {personae.length === 0 && <option value="">{t('createChat.noPersonae')}</option>}
          {personae.length > 0 && filteredPersonae.length === 0 && (
            <option value="">{t('createChat.noMatching')}</option>
          )}
          {filteredPersonae.map((persona) => (
            <option key={persona.id} value={persona.id}>
              {persona.name} — {formatLocale(persona.originalLocale)}
            </option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label htmlFor="chat-student-locale">{t('createChat.iSpeak')}</label>
        <select
          id="chat-student-locale"
          value={studentLocaleIso}
          onChange={(e) => setStudentLocaleIso(e.target.value)}
          required
          disabled={isSubmitting || isLoadingLocales || studentLocales.length === 0}
        >
          {isLoadingLocales && <option value="">{t('createChat.loadingLanguages')}</option>}
          {!isLoadingLocales && studentLocales.length === 0 && (
            <option value="">{t('createChat.noLanguages')}</option>
          )}
          {studentLocales.map((locale) => {
            const iso = localeToIso(locale);
            return (
              <option key={iso} value={iso}>
                {formatLocale(locale)}
              </option>
            );
          })}
        </select>
      </div>
      {error && <div className="error-message">{error}</div>}
      <div className="create-chat-actions">
        <button type="button" className="cancel-button" onClick={onCancel} disabled={isSubmitting}>
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          className="create-button"
          disabled={isSubmitting || !personaId || !studentLocaleIso}
        >
          {isSubmitting ? t('createChat.creating') : t('createChat.create')}
        </button>
      </div>
    </form>
  );
}

// Mirrors dev.c15u.autolang.web.api.dto.chat.ChatListResponse
function ChatList() {
  const { t } = useTranslation();
  const [chats, setChats] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(readShowArchived);
  const navigate = useNavigate();

  const fetchChats = useCallback(async () => {
    setError('');
    setIsLoading(true);

    try {
      // The /api/v0/chat endpoint is protected by JWT (USER_JWT); authedFetch
      // attaches the Bearer access token and refreshes it on a 401.
      // include_archived is resolved server-side rather than by filtering here, so the
      // hidden-by-default guarantee stays in SQL.
      const params = new URLSearchParams({ include_archived: String(showArchived) });
      const response = await authedFetch(`${getBaseUrl()}/api/v0/chat?${params}`, {
        headers: {
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setChats(data.chats || []);
      } else if (response.status === 401) {
        setError(t('chatList.authFailed'));
      } else {
        setError(t('chatList.loadFailed', { status: response.status }));
      }
    } catch (err) {
      setError(t('common.networkError'));
    } finally {
      setIsLoading(false);
    }
  }, [t, showArchived]);

  useEffect(() => {
    fetchChats();
  }, [fetchChats]);

  const toggleShowArchived = (checked) => {
    setShowArchived(checked);
    writeShowArchived(checked);
  };

  const unarchive = useCallback(async (chat, { silent = false } = {}) => {
    try {
      await unarchiveChat(chat.chatId);
      await fetchChats();
      if (!silent) {
        toast(t('chatList.restoredToast', { name: chat.name }));
      }
    } catch (err) {
      toast.error(t('chatList.restoreFailed', { name: chat.name }));
    }
  }, [fetchChats, t]);

  // Unless the toggle is on, the row disappears the moment it is archived, so the
  // toast carries the only affordance to undo it.
  const archive = useCallback(async (chat) => {
    try {
      await archiveChat(chat.chatId);
      await fetchChats();
      toast(t('chatList.archivedToast', { name: chat.name }), {
        action: {
          label: t('common.undo'),
          onClick: () => unarchive(chat, { silent: true }),
        },
      });
    } catch (err) {
      toast.error(t('chatList.archiveFailed', { name: chat.name }));
    }
  }, [fetchChats, t, unarchive]);

  return (
    <div className="chat-list-container">
      <div className="chat-list-header">
        <h2>{t('chatList.title')}</h2>
      </div>
      <div className="chat-list-content">
        <label className="persona-list-show-archived">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(event) => toggleShowArchived(event.target.checked)}
          />
          {t('chatList.showArchived')}
        </label>
        {isLoading && <div className="chat-list-status">{t('chatList.loading')}</div>}
        {error && <div className="error-message">{error}</div>}
        {!isLoading && !error && chats.length === 0 && (
          <div className="chat-list-status">{t('chatList.empty')}</div>
        )}
        {!isLoading && !error && chats.length > 0 && (
          <ul className="chat-list">
            {chats.map((chat) => (
              <li
                key={chat.chatId}
                className="chat-list-item"
                onClick={() => navigate(`/chats/${chat.chatId}`, { state: { chat } })}
              >
                <span className="chat-list-item-main">
                  {chat.persona?.locale?.countryCode && (
                    <span
                      className="chat-list-item-flag"
                      title={formatLocale(chat.persona.locale)}
                    >
                      {countryFlag(chat.persona.locale.countryCode)}
                    </span>
                  )}
                  <span className="chat-list-item-name">
                    {chat.name}
                    {chat.archived && (
                      <span className="persona-list-item-badge">
                        {t('chatList.archived')}
                      </span>
                    )}
                  </span>
                </span>
                <span className="chat-list-item-persona">{chat.persona?.name}</span>
                {/*
                  The whole row is a navigation target, so the action button has to stop
                  the click from bubbling or archiving would also open the chat.
                */}
                {chat.archived ? (
                  <button
                    type="button"
                    className="persona-list-item-action"
                    aria-label={t('chatList.restoreAction', { name: chat.name })}
                    title={t('chatList.restore')}
                    onClick={(event) => {
                      event.stopPropagation();
                      unarchive(chat);
                    }}
                  >
                    <FiRotateCcw aria-hidden="true" />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="persona-list-item-action"
                    aria-label={t('chatList.archiveAction', { name: chat.name })}
                    title={t('chatList.archive')}
                    onClick={(event) => {
                      event.stopPropagation();
                      archive(chat);
                    }}
                  >
                    <FiArchive aria-hidden="true" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// Full page hosting the chat creation form, reached from the "New chat" button on
// the chat list. Returns to the chat list on success or cancel.
function NewChatPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = () => navigate('/chats');

  return (
    <div className="chat-list-container form-page-container">
      <div className="chat-list-header form-page-header">
        <h2>{t('createChat.pageTitle')}</h2>
      </div>
      <div className="chat-list-content">
        <CreateChatForm onCreated={goBack} onCancel={goBack} />
      </div>
    </div>
  );
}

export { NewChatPage };
export default ChatList;
